/**
 * Account Performance — per-account signals relative to the trader's baseline.
 *
 * Surfaces when ONE account's behavior diverges meaningfully from the
 * trader's all-account average. Two patterns:
 *
 *   1. Account WR / P&L drift — this account's performance is materially
 *      worse (or better) than the trader's baseline across all accounts.
 *      Surfaces as warning when this account is dragging, success when
 *      it's an outlier winner.
 *
 *   2. Best / worst setups ON THIS ACCOUNT — same setup may behave
 *      differently per account (size, prop-firm rules, account-specific
 *      psychology). Per-account spotlights make those setups concrete.
 *
 * Why this scope is useful:
 *   The trader runs 7+ accounts simultaneously. Day-scope and setup-scope
 *   analyzers blend everything; an account that's bleeding on a specific
 *   setup gets averaged out across the broader sample. This analyzer
 *   isolates per-account signal.
 *
 * Reads:
 *   trade.accountId, trade.netPnl (must be enriched by scope), trade.setup
 *   ctx.allTrades (full corpus across all accounts) — for baseline
 *   ctx.accountName (scope-supplied label for the title)
 */

import { CATEGORY, SEVERITY, makeObservation } from '../../types.js';

export const key = 'accountPerformance';
export const tier = 'free';

const ACCOUNT_MIN_TRADES   = 8;     // Need this many account trades for a meaningful WR
const BASELINE_MIN_TRADES  = 20;    // Need this many baseline trades for a meaningful comparison
const WR_GAP_FOR_SIGNAL    = 0.15;  // 15pp gap between this account and baseline = surface
const SETUP_MIN_TRADES     = 4;
const SETUP_WR_FLOOR       = 0.30;  // worst-setup must be ≤ this WR to flag
const SETUP_WR_CEIL        = 0.60;  // best-setup must be ≥ this WR to flag

const fmtPct = (n) => `${Math.round(n * 100)}%`;
const fmtMoney = (n) => `${n >= 0 ? '+' : '−'}$${Math.abs(Math.round(n)).toLocaleString()}`;

const isWin = (t) => Number.isFinite(t.netPnl) && t.netPnl > 0;
const wr = (group) => group.length === 0 ? 0 : group.filter(isWin).length / group.length;
const sumNet = (group) => group.reduce((s, t) => s + (Number.isFinite(t.netPnl) ? t.netPnl : 0), 0);

// ── Pattern 1: Account WR/P&L vs trader baseline ──────────────────────────

const detectAccountDrift = (accountId, accountLabel, accountTrades, allTrades) => {
  if (accountTrades.length < ACCOUNT_MIN_TRADES) return null;
  // Baseline = all OTHER accounts (not this one). If the trader only has
  // one account, there's no baseline; bail.
  const baseline = allTrades.filter(t => t.accountId && t.accountId !== accountId);
  if (baseline.length < BASELINE_MIN_TRADES) return null;

  const acctWR = wr(accountTrades);
  const baseWR = wr(baseline);
  const gap = acctWR - baseWR;
  if (Math.abs(gap) < WR_GAP_FOR_SIGNAL) return null;

  const acctNet = sumNet(accountTrades);
  const baseNetPerTrade = baseline.length > 0 ? sumNet(baseline) / baseline.length : 0;
  const expected = baseNetPerTrade * accountTrades.length;
  const variance = acctNet - expected;

  const dragging = gap < 0;
  return makeObservation({
    id: `${key}:${accountId}:${dragging ? 'dragging' : 'outperforming'}`,
    analyzer: key,
    scope: 'account',
    scopeId: accountLabel,
    severity: dragging ? SEVERITY.WARNING : SEVERITY.SUCCESS,
    category: CATEGORY.ACCOUNT,
    title: dragging
      ? `"${accountLabel}" is dragging — ${fmtPct(acctWR)} WR vs ${fmtPct(baseWR)} on your other accounts`
      : `"${accountLabel}" is your standout — ${fmtPct(acctWR)} WR vs ${fmtPct(baseWR)} on your other accounts`,
    detail: dragging
      ? `${accountTrades.length} trades on this account, ${accountTrades.filter(isWin).length} winners. P&L ${fmtMoney(acctNet)}; expected ${fmtMoney(expected)} at your baseline rate (variance ${fmtMoney(variance)}). Same trader, same brain — what's different about this account specifically?`
      : `${accountTrades.length} trades on this account, ${accountTrades.filter(isWin).length} winners. P&L ${fmtMoney(acctNet)}; expected ${fmtMoney(expected)} at your baseline rate (variance ${fmtMoney(variance)}). Whatever you're doing on this account is the template — see what carries over.`,
    action: dragging
      ? `Pull this account's last 10 trades and compare to the same setups on a stronger account. Often it's size, prop-firm rules, or a single repeating mistake on this account specifically.`
      : `Identify what's different about how you trade this account — is it psychological (smaller stakes), structural (different setups available), or process (slower entries)? Replicate it on the others.`,
    evidence: {
      accountId, accountWR: acctWR, baselineWR: baseWR, gap,
      accountTradeCount: accountTrades.length, baselineTradeCount: baseline.length,
      accountNet: acctNet, expectedNet: expected, variance,
    },
    confidence: 0.78,
    priority: dragging ? 82 : 76,
  });
};

// ── Pattern 2: Best / worst setup on this account ─────────────────────────

const detectAccountSetupSpotlights = (accountId, accountLabel, accountTrades) => {
  if (accountTrades.length < ACCOUNT_MIN_TRADES) return [];
  const bySetup = new Map();
  for (const t of accountTrades) {
    if (!t.setup) continue;
    if (!bySetup.has(t.setup)) bySetup.set(t.setup, []);
    bySetup.get(t.setup).push(t);
  }

  const stats = [];
  for (const [setup, group] of bySetup) {
    if (group.length < SETUP_MIN_TRADES) continue;
    const swr = wr(group);
    const net = sumNet(group);
    stats.push({ setup, count: group.length, wr: swr, net });
  }
  if (stats.length < 2) return [];  // Need ≥2 setups to call something the "best" or "worst"

  stats.sort((a, b) => b.net - a.net);
  const best = stats[0];
  const worst = stats[stats.length - 1];

  const out = [];
  if (best.wr >= SETUP_WR_CEIL) {
    out.push(makeObservation({
      id: `${key}:${accountId}:best-setup:${best.setup}`,
      analyzer: key,
      scope: 'account',
      scopeId: accountLabel,
      severity: SEVERITY.SUCCESS,
      category: CATEGORY.SETUP,
      title: `"${best.setup}" is your strongest setup on "${accountLabel}" (${fmtPct(best.wr)} WR, ${fmtMoney(best.net)})`,
      detail: `${best.count} trades on this account, ${best.count - Math.round(best.count * (1 - best.wr))} winners. This setup's edge holds on this account specifically — when you're choosing what to trade here, lean toward it.`,
      action: `Increase frequency of ${best.setup} on this account; consider trading it bigger here than on accounts where its WR isn't as clean.`,
      evidence: { accountId, setup: best.setup, ...best },
      confidence: 0.78,
      priority: 76,
    }));
  }
  if (worst !== best && worst.wr <= SETUP_WR_FLOOR && worst.net < 0) {
    out.push(makeObservation({
      id: `${key}:${accountId}:worst-setup:${worst.setup}`,
      analyzer: key,
      scope: 'account',
      scopeId: accountLabel,
      severity: SEVERITY.WARNING,
      category: CATEGORY.SETUP,
      title: `"${worst.setup}" is bleeding on "${accountLabel}" (${fmtPct(worst.wr)} WR, ${fmtMoney(worst.net)})`,
      detail: `${worst.count} trades on this account, ${worst.count - Math.round(worst.count * (1 - worst.wr))} winners. Either the setup doesn't fit this account's specific constraints (size, rules, hours) or there's a recurring execution leak you only have on this account.`,
      action: `Stop trading ${worst.setup} on this account until you can name what's different. Run the same setup on a stronger account first to confirm it's the account, not the setup.`,
      evidence: { accountId, setup: worst.setup, ...worst },
      confidence: 0.78,
      priority: 80,
    }));
  }
  return out;
};

// ── Public analyze() ──────────────────────────────────────────────────────

export const analyze = (ctx) => {
  const { accountId, accountLabel, accountTrades, allTrades } = ctx || {};
  if (!accountId || !Array.isArray(accountTrades) || accountTrades.length === 0) return null;

  const out = [];
  const drift = detectAccountDrift(accountId, accountLabel, accountTrades, allTrades || []);
  if (drift) out.push(drift);
  const spotlights = detectAccountSetupSpotlights(accountId, accountLabel, accountTrades);
  out.push(...spotlights);
  return out.length === 0 ? null : out;
};

// Exports for tests
export {
  detectAccountDrift,
  detectAccountSetupSpotlights,
  ACCOUNT_MIN_TRADES,
  BASELINE_MIN_TRADES,
  WR_GAP_FOR_SIGNAL,
  SETUP_MIN_TRADES,
  SETUP_WR_FLOOR,
  SETUP_WR_CEIL,
};
